import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Dialog, Modal, Toast } from 'antd-mobile';
import { CameraOutline, DownOutline } from 'antd-mobile-icons';
import Avatar from '@/components/Avatar';
import FormField from '@/components/FormField';
import {
  createCustomer,
  updateCustomer,
  getCustomerDetail,
  CustomerFormData
} from '@/api/customers';
import { uploadImage } from '@/api/upload';
import { compressImage } from '@/utils/image';
import { createVillage, getVillages, Village } from '@/api/villages';
import useBackHandler, { BACK_PRIORITY } from '@/hooks/useBackHandler';
import './styles.css';

interface ImageFile {
  url: string;
}

interface CustomerEditorProps {
  customerId?: number;
  onSaved?: () => void;
  onCancel?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export default function CustomerEditor({
  customerId,
  onSaved,
  onCancel,
  onDirtyChange
}: CustomerEditorProps) {
  const isEdit = Boolean(customerId);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const avatarPreviewRef = useRef<string | null>(null);
  const [name, setName] = useState('');
  const [village, setVillage] = useState('');
  const [phone, setPhone] = useState('');
  const [fileList, setFileList] = useState<ImageFile[]>([]);
  const [avatarPreview, setAvatarPreview] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [villages, setVillages] = useState<Village[]>([]);
  const [villageModalVisible, setVillageModalVisible] = useState(false);
  const [villageSearch, setVillageSearch] = useState('');
  const [errors, setErrors] = useState<{ name?: string; village?: string }>({});

  useBackHandler(
    villageModalVisible,
    () => setVillageModalVisible(false),
    BACK_PRIORITY.modal
  );

  const markDirty = () => onDirtyChange?.(true);

  useEffect(() => {
    loadVillages();
    if (isEdit && customerId) loadCustomer();
  }, [customerId, isEdit]);

  useEffect(() => () => {
    if (avatarPreviewRef.current) URL.revokeObjectURL(avatarPreviewRef.current);
  }, []);

  const clearAvatarPreview = () => {
    if (avatarPreviewRef.current) URL.revokeObjectURL(avatarPreviewRef.current);
    avatarPreviewRef.current = null;
    setAvatarPreview(undefined);
  };

  const loadVillages = async () => {
    try {
      const res = await getVillages();
      if (res.code === 0) setVillages(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const loadCustomer = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const res = await getCustomerDetail(customerId);
      if (res.code === 0) {
        const { customer } = res.data;
        setName(customer.name);
        setVillage(customer.village || '');
        setPhone(customer.phone || '');
        setFileList(customer.avatar ? [{ url: customer.avatar }] : []);
      }
    } catch (error) {
      console.error(error);
      Toast.show({ content: '加载客户信息失败', icon: 'fail' });
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    clearAvatarPreview();
    const previewUrl = URL.createObjectURL(file);
    avatarPreviewRef.current = previewUrl;
    setAvatarPreview(previewUrl);
    setAvatarUploading(true);
    try {
      const compressed = await compressImage(file);
      const res = await uploadImage(compressed);
      if (res.code !== 0 || !res.data?.url) {
        throw new Error(res.message || '上传结果异常，请重试');
      }
      setFileList([{ url: res.data.url }]);
      clearAvatarPreview();
      markDirty();
    } catch (error: any) {
      let message = error?.response?.data?.message || error.message || '上传失败';
      const normalizedMessage = String(message).toLowerCase();
      if (error?.code === 'ECONNABORTED' || normalizedMessage.includes('timeout')) {
        message = '网络较慢，请重试';
      } else if (normalizedMessage.includes('network error')) {
        message = '网络连接失败，请检查网络后重试';
      } else if (message.includes('不支持的文件格式')) {
        message = '图片格式不支持，请换一张试试';
      }
      Toast.show({ content: message, icon: 'fail' });
      clearAvatarPreview();
    } finally {
      setAvatarUploading(false);
    }
  };

  const validate = () => {
    const nextErrors: { name?: string } = {};
    if (!name.trim()) nextErrors.name = '请输入姓名';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSaveCustomer = async (villageName: string) => {
    const payload: CustomerFormData = {
      name: name.trim(),
      village: villageName.trim(),
      phone: phone.trim() || undefined,
      avatar: fileList[0]?.url
    };

    setSaving(true);
    try {
      if (isEdit && customerId) {
        await updateCustomer(customerId, payload);
        Toast.show({ content: '更新成功', icon: 'success' });
      } else {
        await createCustomer(payload);
        Toast.show({ content: '创建成功', icon: 'success' });
      }
      onSaved?.();
    } catch (error) {
      console.error(error);
      Toast.show({ content: '保存失败', icon: 'fail' });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!validate()) {
      Toast.show({ content: '请完善信息', icon: 'fail' });
      return;
    }

    const trimmedVillage = village.trim();
    const existingVillage = villages.find((item) => item.name === trimmedVillage);

    if (trimmedVillage && !existingVillage) {
      const result = await Dialog.confirm({
        title: '保存新村？',
        content: `「${trimmedVillage}」还不存在，是否保存到村字典？`
      });
      if (result) {
        try {
          await createVillage(trimmedVillage);
          await loadVillages();
        } catch (error) {
          console.error(error);
        }
      }
    }

    await handleSaveCustomer(trimmedVillage);
  };

  const filteredVillages = villages.filter((item) =>
    item.name.toLowerCase().includes(villageSearch.toLowerCase())
  );

  return (
    <div className="customer-editor">
      {loading ? (
        <div className="customer-editor-loading" aria-label="正在加载客户信息">
          <div className="ios-skeleton" />
          <div className="ios-skeleton" />
        </div>
      ) : (
        <>
          <motion.div
            className="customer-editor-avatar-area"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.22 }}
          >
            <button
              type="button"
              className="customer-editor-avatar-button"
              onClick={() => avatarInputRef.current?.click()}
              disabled={avatarUploading}
              aria-label={fileList[0]?.url ? '更换客户头像' : '上传客户头像'}
            >
              <Avatar
                src={avatarPreview || fileList[0]?.url}
                name={name}
                size={100}
                style={{
                  border: '2px solid #e9e5e2',
                  boxShadow: 'none'
                }}
              />
              <span className="customer-editor-camera" aria-hidden="true">
                <CameraOutline />
              </span>
              {avatarUploading && <span className="customer-editor-avatar-loading">上传中</span>}
            </button>
            <input
              ref={avatarInputRef}
              className="customer-editor-avatar-input"
              type="file"
              accept="image/*,.heic,.heif"
              onChange={handleAvatarChange}
            />
            <span className="customer-editor-avatar-label">更换头像</span>
          </motion.div>

          <div className="customer-editor-fields">
            <FormField label="姓名" error={errors.name} className="customer-editor-field">
              <input
                className="customer-editor-input"
                placeholder="请输入客户姓名"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  markDirty();
                  if (errors.name) setErrors((current) => ({ ...current, name: undefined }));
                }}
              />
              <p className="customer-editor-hint">用于客户识别与记录</p>
            </FormField>

            <FormField label="村" error={errors.village} className="customer-editor-field">
              <button
                type="button"
                className={`customer-editor-select${village ? '' : ' is-placeholder'}`}
                onClick={() => {
                  setVillageSearch(village);
                  setVillageModalVisible(true);
                }}
              >
                <span>{village || '请选择或输入新村'}</span>
                <DownOutline aria-hidden="true" />
              </button>
            </FormField>

            <FormField label="电话" className="customer-editor-field">
              <input
                className="customer-editor-input"
                type="tel"
                inputMode="tel"
                maxLength={11}
                placeholder="可选"
                value={phone}
                onChange={(event) => {
                  setPhone(event.target.value);
                  markDirty();
                }}
              />
              <p className="customer-editor-hint">用于客户识别与记录</p>
            </FormField>
          </div>

          <p className="customer-editor-privacy">客户信息仅用于销售记录管理</p>
        </>
      )}

      <div className="customer-editor-actions">
        <motion.button
          type="button"
          className="customer-editor-action customer-editor-action--cancel"
          onClick={onCancel}
          disabled={saving}
          whileTap={{ scale: 0.98 }}
          data-haptic="light"
        >
          取消
        </motion.button>
        <motion.button
          type="button"
          className="customer-editor-action customer-editor-action--save"
          onClick={handleSubmit}
          disabled={saving || loading || avatarUploading}
          whileTap={{ scale: saving ? 1 : 0.98 }}
          data-haptic="medium"
        >
          {saving ? '保存中' : '保存'}
        </motion.button>
      </div>

      <Modal
        visible={villageModalVisible}
        title="选择村"
        content={
          <div className="customer-editor-village-modal">
            <input
              className="ios-input"
              placeholder="搜索或输入新村"
              value={villageSearch}
              onChange={(event) => setVillageSearch(event.target.value)}
            />
            <div className="customer-editor-village-list">
              {filteredVillages.length === 0 ? (
                <div className="customer-editor-village-empty">未找到，将保存为新村</div>
              ) : (
                filteredVillages.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className="customer-editor-village-option"
                    onClick={() => {
                      setVillage(item.name);
                      markDirty();
                      setVillageModalVisible(false);
                      if (errors.village) {
                        setErrors((current) => ({ ...current, village: undefined }));
                      }
                    }}
                  >
                    {item.name}
                  </button>
                ))
              )}
            </div>
          </div>
        }
        closeOnAction
        onClose={() => setVillageModalVisible(false)}
        actions={[
          {
            key: 'cancel',
            text: '取消',
            onClick: () => setVillageModalVisible(false)
          },
          {
            key: 'confirm',
            text: '确定',
            primary: true,
            onClick: () => {
              setVillage(villageSearch);
              markDirty();
              setVillageModalVisible(false);
              if (errors.village) {
                setErrors((current) => ({ ...current, village: undefined }));
              }
            }
          }
        ]}
      />
    </div>
  );
}
